import { useState, type AnchorHTMLAttributes, type ReactNode, type MouseEvent } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { linkIntent, viewableUrl } from "@/lib/open-url";

interface Props extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href" | "onClick"> {
  href: string;
  children: ReactNode;
  onOpen?: () => void;
  onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

export function SafeExternalLink({ href, children, onOpen, onClick, className, ...rest }: Props) {
  const [open, setOpen] = useState(false);
  const risky = linkIntent(href) === "risky";

  if (!risky) {
    return (
      <a
        {...rest}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={(e) => {
          onClick?.(e);
          onOpen?.();
        }}
      >
        {children}
      </a>
    );
  }

  return (
    <>
      <a
        {...rest}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClick?.(e);
          setOpen(true);
        }}
      >
        {children}
      </a>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-paper border-rule">
          <DialogHeader className="space-y-3">
            <p className="eyebrow">Et øjeblik</p>
            <DialogTitle className="font-serif text-[1.6rem] leading-[1.2] text-ink font-normal">
              Denne kilde vil måske hentes ned
            </DialogTitle>
            <DialogDescription className="text-ink-soft leading-relaxed">
              Linket kan starte en download i stedet for at åbne som en almindelig side. Vælg selv, hvordan du vil se den.
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-ink-soft break-all border-l-2 border-bordeaux/40 pl-3 py-1 font-mono">
            {href}
          </p>
          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="btn-base text-ink-soft hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bordeaux/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
            >
              Annullér
            </button>
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { onOpen?.(); setOpen(false); }}
              className="btn-base btn-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bordeaux/40 focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
            >
              Hent filen
            </a>
            <a
              href={viewableUrl(href)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => { onOpen?.(); setOpen(false); }}
              className="btn-base btn-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bordeaux focus-visible:ring-offset-2 focus-visible:ring-offset-paper"
            >
              Åbn online
            </a>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
